/**
 * Bloom mask: which surfaces may glow.
 *
 * The post pipeline renders an extra 8-bit MRT channel, BLOOM_MASK, whose value comes from
 * bloomMaskNode: 1 by default, or whatever a material set with setBloomMask(). Bloom reads
 * sceneColor × mask, then applies a luminance threshold. Artworks set 0 so a bright artwork
 * never becomes a light source: the art keeps its true colour and the void stays black.
 * Glow comes from deliberate things (frames, lights, wet highlights, neon).
 *
 * Found in the 3 Nov spike: bloom fed from all emissive washed the art pink and lifted
 * the void to magenta haze, even at strength 0.25.
 *
 * Why not material.mrtNode: a material MRT override with no pass MRT (?post=0, or a future
 * no-post phone preset) leaves the shader with an empty output struct and it fails to
 * compile. Storing the mask on the material and resolving it inside the pass's MRT node
 * means materials render correctly with or without the post pipeline.
 */
import * as THREE from 'three/webgpu'
import { Fn, float, mrt } from 'three/tsl'

export const BLOOM_MASK = 'bloom'

const KEY = 'rtkBloomMask'

/** Any float node (step(), smoothstep(), a uniform...). TSL's own types are too narrow here. */
type MaskNode = Parameters<typeof mrt>[0][string]

/** Mark a material's bloom mask. 0 = never blooms, 1 = default. Set before first render. */
export function setBloomMask(material: THREE.Material, mask: MaskNode | number) {
  material.userData[KEY] = typeof mask === 'number' ? float(mask) : mask
}

/**
 * Resolves per material at shader build time: the builder knows which material it's
 * compiling, so one MRT entry on the pass serves every material.
 */
export const bloomMaskNode = Fn((builder: { material?: THREE.Material | null }) => {
  const mask = builder.material?.userData?.[KEY] as MaskNode | undefined
  return mask ?? float(1)
})
